import { PostHog } from "posthog-js";
import { useEffect } from "react";

import { TOrganisationDetails } from "graphql/organisation";
import { findOrganisation } from "selectors/organisation";
import { FeatureFlag, isEnabledFeature } from "utils/featureFlags";

import { getLocaleCookie } from "../locale/utils/cookies";

type UsePostHogIdentifyProps = {
  numberUuid?: string;
  organisationUuid?: string;
  organisations?: TOrganisationDetails[];
  loading: boolean;
  posthog?: PostHog;
};

const safePostHogCall = (label: string, fn: () => void) => {
  try {
    fn();
  } catch (e) {
    console.error(`PostHog ${label} failed`, e);
  }
};

export const usePostHogIdentify = ({
  numberUuid,
  organisationUuid,
  organisations,
  loading,
  posthog,
}: UsePostHogIdentifyProps) => {
  const locale = getLocaleCookie();

  useEffect(() => {
    const enabled = isEnabledFeature(FeatureFlag.POSTHOG_PRODUCT_ANALYTICS);
    if (!enabled || !posthog || !organisationUuid || !numberUuid || loading || !organisations?.length) return;

    try {
      const organisation = findOrganisation(organisations, organisationUuid);
      const number = organisation?.numbers?.find((n) => n.uuid === numberUuid);
      const account = organisation?.myAccount;

      if (!organisation || !account) return;

      // identify the user
      safePostHogCall("identify", () => {
        posthog.identify(account.uuid, {
          name: account.uuid,
          organisation: {
            uuid: organisationUuid,
            name: organisation.name ?? "unknown",
          },
          number: {
            uuid: numberUuid,
            name: number?.vname ?? "unknown",
          },
        });
      });

      // group by organisation
      safePostHogCall("group organisation", () => {
        posthog.group("organisation", organisation.uuid, {
          name: organisation.name ?? "unknown",
        });
      });

      // group by number
      safePostHogCall("group number", () => {
        if (number?.uuid) {
          posthog.group("number", number.uuid, {
            name: number.vname ?? "unknown",
          });
        }
      });

      // group by locale
      safePostHogCall("group locale", () => {
        if (number?.uuid) {
          posthog.group("locale", locale, {
            name: locale ?? "unknown",
          });
        }
      });
    } catch (e) {
      console.error("PostHog analytics wrapper failed", e);
    }
  }, [organisationUuid, numberUuid, organisations, loading, posthog, locale]);
};
